import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { fetchOrderResults } from '@/api/order';
import { getToken } from '@/lib/auth-storage';
import type { AppOrderResults, MhOrderResultRow } from '@/types/mh';
import { getOrderStatusStyle } from '@/utils/order-display';
import { resolveAssetUrl } from '@/utils/product-display';

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'];

type ResultFile = {
  raw: string;
  url: string;
  name: string;
  isImage: boolean;
};

function fileNameFromPath(path: string): string {
  const clean = path.split('?')[0] || '';
  const parts = clean.split('/');
  try {
    return decodeURIComponent(parts[parts.length - 1] || '文件');
  } catch {
    return parts[parts.length - 1] || '文件';
  }
}

function parseStoredUrls(raw?: string | null): string[] {
  const value = String(raw || '').trim();
  if (!value) {
    return [];
  }
  try {
    const parsed = JSON.parse(value) as unknown;
    if (Array.isArray(parsed)) {
      return parsed.map((item) => String(item || '').trim()).filter(Boolean);
    }
  } catch {
    // Historical rows may store comma-separated paths.
  }
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function toFiles(raw?: string | null): ResultFile[] {
  return parseStoredUrls(raw).map((item) => {
    const name = fileNameFromPath(item);
    const ext = name.includes('.') ? name.split('.').pop()?.toLowerCase() || '' : '';
    return {
      raw: item,
      url: resolveAssetUrl(item),
      name,
      isImage: IMAGE_EXTENSIONS.includes(ext),
    };
  });
}

function ResultRecord({ row }: { row: MhOrderResultRow }) {
  const images = toFiles(row.imageUrl);
  const files = toFiles(row.fileUrl);

  return (
    <article className="bg-white rounded-2xl border border-gray-100 p-5">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            {row.resultRemark || '查询反馈'}
          </h2>
          <p className="text-xs text-gray-400 mt-1">{row.createTime || '—'}</p>
        </div>
      </div>

      {row.textContent && (
        <div className="text-sm text-gray-700 leading-6 whitespace-pre-wrap bg-gray-50 rounded-xl px-4 py-3 mb-4">
          {row.textContent}
        </div>
      )}

      {images.length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3">图片结果</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {images.map((file) => (
              <a
                key={file.raw}
                href={file.url}
                target="_blank"
                rel="noreferrer"
                className="block rounded-xl overflow-hidden bg-gray-50 border border-gray-100"
              >
                <img src={file.url} alt={file.name} className="w-full aspect-square object-cover" />
              </a>
            ))}
          </div>
        </div>
      )}

      {files.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-3">文件附件</h3>
          <div className="space-y-2">
            {files.map((file) => (
              <a
                key={file.raw}
                href={file.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 hover:bg-gray-100 transition-colors"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <i className={file.isImage ? 'ri-image-line' : 'ri-file-text-line'} />
                  <span className="text-sm text-gray-700 truncate">{file.name}</span>
                </span>
                <span className="text-xs text-gray-400 flex-shrink-0">打开</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}

export default function OrderResultsPage() {
  const { orderNo = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState<AppOrderResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!getToken()) {
      const redirect = `${location.pathname}${location.search}`;
      navigate(`/login?redirect=${encodeURIComponent(redirect)}`, { replace: true });
      return;
    }
    if (!orderNo) {
      setLoadError('缺少订单号');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError('');
    fetchOrderResults(orderNo)
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 0 || !res.data) {
          setLoadError(res.msg || '加载失败');
          return;
        }
        setData(res.data);
      })
      .catch(() => {
        if (!cancelled) setLoadError('网络异常');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, navigate, orderNo]);

  const statusStyle = useMemo(() => getOrderStatusStyle(data?.status || ''), [data?.status]);
  const records = data?.results || [];
  const waiting = data && !data.visible;

  return (
    <div className="min-h-screen flex flex-col bg-[#f7f7f8]">
      <Navbar />
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-8 pb-24 md:pb-8">
        <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
          <Link to="/" className="hover:text-gray-700 transition-colors">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/orders" className="hover:text-gray-700 transition-colors">
            我的订单
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-gray-700 font-medium">查询结果</span>
        </div>

        <section className="bg-white rounded-2xl border border-gray-100 p-6 mb-5">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900 mb-2">查询结果</h1>
              <p className="text-sm text-gray-500">
                订单号：<span className="font-mono font-medium text-gray-800">{orderNo}</span>
              </p>
              {data?.productName && (
                <p className="text-sm text-gray-500 mt-1">
                  产品：<span className="font-medium text-gray-800">{data.productName}</span>
                </p>
              )}
            </div>
            {data?.status && (
              <span
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium ${statusStyle.className}`}
              >
                <i className={statusStyle.icon} />
                {statusStyle.label}
              </span>
            )}
          </div>
          {data?.orderResult && (
            <p className="text-sm text-gray-600 bg-gray-50 rounded-xl px-4 py-3 mt-4">
              结果摘要：{data.orderResult}
            </p>
          )}
        </section>

        {loading && <div className="py-20 text-center text-gray-500 text-sm">加载结果中…</div>}

        {!loading && loadError && (
          <div className="py-16 text-center rounded-2xl border border-dashed border-gray-200 bg-white">
            <p className="text-gray-600 mb-4">{loadError}</p>
            <Link to="/orders" className="inline-flex text-sm font-medium text-gray-900 underline">
              返回我的订单
            </Link>
          </div>
        )}

        {!loading && !loadError && waiting && (
          <div className="py-16 text-center rounded-2xl border border-dashed border-gray-200 bg-white">
            <i className="ri-loader-4-line text-3xl text-gray-300" />
            <p className="text-gray-700 font-medium mt-3">订单处理中，等待后台反馈结果</p>
            <p className="text-sm text-gray-400 mt-1">结果上传后会在这里展示文字、图片和文件附件。</p>
          </div>
        )}

        {!loading && !loadError && data?.visible && records.length === 0 && (
          <div className="py-16 text-center rounded-2xl border border-dashed border-gray-200 bg-white">
            <p className="text-gray-600">暂无可展示的结果附件，请稍后查看。</p>
          </div>
        )}

        {!loading && !loadError && data?.visible && records.length > 0 && (
          <div className="space-y-4">
            {records.map((row) => (
              <ResultRecord key={row.resultId} row={row} />
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
